/**
 * Renders the folded timeline. Every item kind has a case here; the
 * exhaustiveness check at the bottom means adding one without a renderer is a
 * compile error, not a blank patch of screen.
 */
import { Loader2 } from 'lucide-react'
import { useCallback, useMemo } from 'react'
import { Markdown } from '@/components/Markdown'
import { Avatar } from '@/components/ui/display'
import type { MessageItem, TimelineItem } from '@/data/chat'
import {currentPerson } from '@/data/people'
import { cn } from '@/lib/utils'
import { AgentSteps, type Step } from './AgentSteps'
import { CitationList, InlineCitation } from './Citation'

type Row =
  | { kind: 'item'; id: string; item: TimelineItem }
  | { kind: 'steps'; id: string; steps: Step[] }

/**
 * Folds the flat item list into rows.
 *
 * The agent's WORK — tool calls and reasoning — collapses into one `AgentSteps`
 * row per turn; the answer renders below it at full size.
 *
 * The split is per FIELD: an assistant message carries both a reasoning summary
 * (work) and the answer (not work), and only TEXT makes it an answer. Treating
 * "has citations" as "is an answer" would flush a textless message into its own
 * row and render a bare source list between two step headers, because citations
 * are attached the moment the search resolves.
 */
export function toRows(items: TimelineItem[]): Row[] {
  const rows: Row[] = []
  let steps: Step[] = []

  const flushSteps = () => {
    if (steps.length === 0) return
    rows.push({ kind: 'steps', id: `steps-${steps[0]!.id}`, steps })
    steps = []
  }

  for (const item of items) {
    if (item.kind === 'tool') {
      steps.push({ id: item.id, kind: 'tool', item })
      continue
    }
    if (item.role === 'assistant') {
      if (item.thinking.trim() && !steps.some((step) => step.id === `${item.id}-reasoning`)) {
        steps.push({ id: `${item.id}-reasoning`, kind: 'reasoning', text: item.thinking })
      }
      if (item.text.trim().length > 0) {
        flushSteps()
        rows.push({ kind: 'item', id: item.id, item })
      }
      continue
    }
    flushSteps()
    rows.push({ kind: 'item', id: item.id, item })
  }
  flushSteps()
  return rows
}

export function Timeline({ items, running }: { items: TimelineItem[]; running: boolean }) {
  const rows = useMemo(() => toRows(items), [items])

  return (
    <div className="space-y-5">
      {rows.map((row, index) => {
        if (row.kind === 'steps') {
          // Only the last row can still be running: once a later row exists,
          // this group's work is finished.
          return (
            <AgentSteps
              key={row.id}
              steps={row.steps}
              running={running && index === rows.length - 1}
            />
          )
        }
        const item = row.item
        switch (item.kind) {
          case 'message':
            return <Message key={item.id} item={item} />
          case 'tool':
            // Unreachable — tools are folded above — but the exhaustiveness
            // check below only holds if every kind has a case.
            return null
          default: {
            const _never: never = item
            void _never
            return null
          }
        }
      })}
    </div>
  )
}

/**
 * The sentences in an answer that carry a given reference.
 *
 * This is what the citation is FOR — the claim the reader wants backed — and it
 * is what the panel shows above the meeting so the evidence arrives with the
 * thing it is evidence for.
 *
 * All occurrences, not the nth: a marker can repeat, and `renderCitation` is
 * called per marker without being told which occurrence it is.
 */
export function claimFor(text: string, ref: string): string | undefined {
  const marker = `[[${ref}]]`
  if (!text.includes(marker)) return undefined

  const strip = (part: string) =>
    part.replace(/\[\[\d{1,9}(?:#\d{1,6}|\.\d{1,7}(?:-\d{1,7})?)?\]\]/g, ' ').replace(/\s+/g, ' ').trim()
  const parts = text
    .split(/(?<=[.!?:])\s+|\n+/)
    .map((part) => part.trim())
    .filter(Boolean)

  const claims = parts.flatMap((part, i) => {
    if (!part.includes(marker)) return []
    const stripped = strip(part)
    // A marker written AFTER the full stop lands in a fragment of its own once
    // the sentence has been split on that stop, leaving nothing behind. The
    // claim is then the sentence it follows — which is where models put it most
    // of the time, so without this the panel opened with no quote at all.
    return [stripped || strip(parts[i - 1] ?? '')]
  })

  const claim = claims.filter(Boolean).join(' ').trim()
  return claim.length > 0 ? claim : undefined
}

function Message({ item }: { item: MessageItem }) {
  /**
   * Resolves a `[[12.137]]` the model wrote to the source behind it.
   *
   * By reference, not by position. The service sends only the references it
   * actually vouches for, so the list is not a dense `1..n` and indexing into
   * it would quietly attach the wrong meeting to a claim — the one failure mode
   * a citation must not have.
   *
   * An unknown reference renders as literal text rather than a chip: a model
   * can always write a number nobody showed it, and a chip pointing at nothing
   * is worse than a visible `[[47.3]]`.
   */
  const byRef = useMemo(
    () => new Map(item.citations.map((citation) => [citation.ref, citation])),
    [item.citations]
  )

  const renderCitation = useCallback(
    (ref: string) => {
      const citation = byRef.get(`[[${ref}]]`)
      if (!citation) return <>[[{ref}]]</>
      return <InlineCitation citation={citation} claim={claimFor(item.text, ref)} />
    },
    [byRef, item.text]
  )

  if (item.role === 'user') {
    return (
      <div className="flex justify-end" aria-label="Your message">
        <div className="flex max-w-[85%] items-start gap-2.5">
          <div className="rounded-[var(--radius-xl)] rounded-tr-[var(--radius-xs)] bg-[var(--color-surface-sunken)] px-3.5 py-2.5">
            <p className="text-sm leading-relaxed whitespace-pre-wrap text-[var(--color-fg)]">
              {item.text}
            </p>
          </div>
          <Avatar name={currentPerson().name} size="sm" className="mt-0.5" />
        </div>
      </div>
    )
  }

  return (
    <div className="min-w-0" aria-label="Assistant message">
      {/* Reasoning is not rendered here — it belongs to the folded steps row
          above, which is where the turn's working lives. */}
      {item.text ? (
        <div
          className={cn('min-w-0 text-sm text-[var(--color-fg)]', item.streaming && 'streaming-caret')}
        >
          <Markdown streaming={item.streaming} renderCitation={renderCitation}>
            {item.text}
          </Markdown>
        </div>
      ) : item.streaming ? (
        <div className="flex items-center gap-2 text-xs text-[var(--color-fg-subtle)]">
          <Loader2 className="size-3 animate-spin" />
          Loading…
        </div>
      ) : null}
      {item.citations.length > 0 && item.text ? <CitationList citations={item.citations} /> : null}
    </div>
  )
}
